import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CalendarRange, Users, MapPin, CreditCard, Banknote, Loader2, CheckCircle2,
  ArrowLeft, Tent, Mail, Wallet, AlertTriangle, Check, Plus,
} from "lucide-react";
import { MiniSiteRatingBadge } from "@/components/public/MiniSiteReviews";

const EXTRA_UNIT_LABEL = {
  per_stay: "par séjour",
  per_night: "par nuit",
  per_person: "par personne",
  per_person_per_night: "par personne et par nuit",
};

const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function PublicReservation() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [dates, setDates] = useState({ from: plusDays(7), to: plusDays(9) });
  const [guests, setGuests] = useState(2);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [picks, setPicks] = useState({});
  const [form, setForm] = useState({ client_nom: "", client_email: "", client_telephone: "", message: "" });
  const [mode, setMode] = useState("onsite");
  const [busy, setBusy] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");

  const load = async () => {
    try {
      const { data: d } = await api.get(
        `/public/sites/${code}/reservation?date_from=${dates.from}&date_to=${dates.to}`);
      setData(d);
      const p = d.payments || {};
      setMode(p.card ? "online" : p.paypal ? "paypal" : "onsite");
    } catch (e) {
      setData({ error: formatApiError(e.response?.data?.detail) });
    }
  };
  useEffect(() => { load(); setSelected(null); setQuote(null); setQuoteError(""); setPicks({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, dates.from, dates.to]);

  const extraPicks = useMemo(
    () => Object.entries(picks).filter(([, q]) => q > 0).map(([id, qty]) => ({ id, qty })),
    [picks]);
  const extraPicksKey = JSON.stringify(extraPicks);

  const cfg = data?.config;
  const pay = data?.payments || {};
  const labels = data?.labels || { unit: "Hébergement", units: "Hébergements" };
  const nights = data?.nights;

  const quoteFor = async (u, list) => {
    setQuoteError("");
    try {
      const { data: q } = await api.post(`/public/sites/${code}/reservation/quote`, {
        unit_id: u.id, date_from: dates.from, date_to: dates.to,
        guests: parseInt(guests) || 1, extras: list,
      });
      setQuote(q);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setQuoteError(msg);
      toast.error(msg);
      setQuote(null);
      setSelected(null);
    }
  };

  const pick = async (u) => {
    setSelected(u);
    setQuote(null);
    await quoteFor(u, extraPicks);
  };

  // Le client coche une option → nouveau devis serveur (jamais de calcul côté navigateur)
  useEffect(() => {
    if (selected) quoteFor(selected, extraPicks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraPicksKey]);

  const submit = async () => {
    if (!form.client_nom.trim() || !form.client_email.includes("@")) {
      toast.error("Indiquez votre nom et un email valide"); return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/sites/${code}/reservation`, {
        unit_id: selected.id, date_from: dates.from, date_to: dates.to,
        guests: parseInt(guests) || 1, ...form,
        extras: extraPicks,
        payment_mode: mode, origin_url: window.location.origin,
      });
      if (r.checkout_url) { window.location.assign(r.checkout_url); return; }
      navigate(`/sites/${code}/reservation/${r.booking.qr_token}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestLink = async () => {
    if (!linkEmail.includes("@")) { toast.error("Email invalide"); return; }
    try {
      await api.post(`/public/sites/${code}/client/request-link`, {
        email: linkEmail, origin_url: window.location.origin,
      });
      toast.success("Si des réservations existent à cette adresse, vous recevez un lien d'accès par email.");
      setLinkEmail("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const available = useMemo(() => (data?.units || []).filter((u) => u.available !== false), [data]);

  if (data?.error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6"
           data-testid="pr-unavailable">
        <div className="max-w-md text-center">
          <Tent className="h-10 w-10 mx-auto text-[#FFD700] mb-3" />
          <div className="font-display font-black text-2xl tracking-tight">Réservation indisponible</div>
          <div className="text-sm text-white/60 mt-2">{data.error}</div>
          <Button variant="outline" className="mt-4" onClick={() => navigate(`/sites/${code}`)} data-testid="pr-back">
            <ArrowLeft className="h-4 w-4 mr-1" />Retour au site
          </Button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white/50 p-10" data-testid="pr-loading">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="public-reservation-page">
      <div className="max-w-5xl mx-auto p-5 lg:p-10">
        <button onClick={() => navigate(`/sites/${code}`)} data-testid="pr-back-link"
                className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-1 mb-4">
          <ArrowLeft className="h-3 w-3" />{data.entreprise?.name}
        </button>

        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
          Réserver
        </h1>
        <p className="text-base text-white/60 mt-2 max-w-xl">
          Choisissez vos dates, votre {labels.unit.toLowerCase()}, et recevez votre QR code
          d'entrée par email. Arrivée à partir de {cfg?.checkin_time} · départ avant {cfg?.checkout_time}.
        </p>
        <div className="mt-4"><MiniSiteRatingBadge code={code} onClick={() => navigate(`/sites/${code}#avis`)} /></div>

        {/* Dates */}
        <div className="mt-7 border border-white/15 bg-white/5 p-4 grid sm:grid-cols-4 gap-3" data-testid="pr-dates">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-white/50">Arrivée</Label>
            <Input type="date" min={today()} value={dates.from} data-testid="pr-date-from"
                   className="bg-[#0A0A0A] border-white/20 text-white"
                   onChange={(e) => setDates({ ...dates, from: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-white/50">Départ</Label>
            <Input type="date" min={dates.from} value={dates.to} data-testid="pr-date-to"
                   className="bg-[#0A0A0A] border-white/20 text-white"
                   onChange={(e) => setDates({ ...dates, to: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-white/50">Voyageurs</Label>
            <Input type="number" min="1" max="20" value={guests} data-testid="pr-guests"
                   className="bg-[#0A0A0A] border-white/20 text-white"
                   onChange={(e) => { setGuests(e.target.value); setSelected(null); setQuote(null); }} />
          </div>
          <div className="flex items-end">
            <div className="text-sm" data-testid="pr-summary">
              <div className="font-display font-black text-2xl leading-none">{nights || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/50">
                nuit{(nights || 0) > 1 ? "s" : ""} · {available.length} dispo
              </div>
            </div>
          </div>
        </div>
        {cfg?.min_nights > 1 && (
          <div className="text-[11px] text-[#FFD700] mt-1" data-testid="pr-min-nights">
            Séjour minimum : {cfg.min_nights} nuits.
          </div>
        )}
        {quoteError && (
          <div className="mt-3 border border-[#F59E0B] bg-[#F59E0B]/10 p-3 text-sm flex items-start gap-2"
               data-testid="pr-quote-error">
            <AlertTriangle className="h-4 w-4 text-[#F59E0B] shrink-0 mt-0.5" />
            <span>{quoteError} <span className="text-white/50">Ajustez vos dates ci-dessus.</span></span>
          </div>
        )}

        {/* Plan du camping */}
        {cfg?.kind === "camping" && cfg?.map_photo_url && (
          <div className="mt-6" data-testid="pr-map">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2 flex items-center gap-1">
              <MapPin className="h-3 w-3" />Plan du camping — cliquez sur un emplacement
            </div>
            <div className="relative inline-block border border-white/20 max-w-full">
              <img src={cfg.map_photo_url} alt="Plan du camping"
                   className="block max-w-full max-h-[420px] object-contain" />
              {(data.units || []).filter((u) => u.map_x != null && u.map_y != null).map((u) => (
                <button key={u.id} type="button" data-testid={`pr-pin-${u.id}`}
                        disabled={u.available === false}
                        onClick={() => pick(u)}
                        style={{ left: `${u.map_x}%`, top: `${u.map_y}%` }}
                        className={`absolute -translate-x-1/2 -translate-y-1/2 text-[9px] font-bold px-1.5 py-0.5 border transition-transform hover:scale-125 ${
                          u.available === false
                            ? "bg-[#868E96] text-white/70 border-white/30 cursor-not-allowed"
                            : selected?.id === u.id
                              ? "bg-[#FFD700] text-black border-black scale-125"
                              : "bg-[#059669] text-white border-white"}`}
                        title={`${u.label} · ${eur(u.price_per_night)}/nuit`}>
                  {u.label.replace(/[^0-9]/g, "") || u.label.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Unités */}
        <div className="mt-7">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2">
            {labels.units} disponibles
          </div>
          {available.length === 0 ? (
            <div className="border border-white/15 bg-white/5 p-8 text-center text-sm text-white/60"
                 data-testid="pr-none-available">
              Rien de libre sur ces dates. Essayez d'autres dates.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="pr-units">
              {available.map((u) => (
                <button key={u.id} type="button" onClick={() => pick(u)} data-testid={`pr-unit-${u.id}`}
                        className={`text-left border p-4 transition-colors ${
                          selected?.id === u.id
                            ? "border-[#FFD700] bg-[#FFD700]/10"
                            : "border-white/15 bg-white/5 hover:border-white/40"}`}>
                  {u.photo_url && <img src={u.photo_url} alt={u.label} className="w-full h-28 object-cover mb-2" />}
                  <div className="font-display font-bold text-lg leading-tight">{u.label}</div>
                  <div className="text-[11px] text-white/50 flex items-center gap-2 mt-0.5">
                    <Users className="h-3 w-3" />{u.capacity} pers.
                    {u.unit_type && <span>· {u.unit_type}</span>}
                  </div>
                  {u.description && <div className="text-[11px] text-white/60 mt-1 line-clamp-2">{u.description}</div>}
                  {(u.amenities || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {u.amenities.slice(0, 4).map((a) => (
                        <span key={a} className="text-[9px] uppercase tracking-wider border border-white/20 px-1.5 py-0.5 text-white/60">{a}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 font-display font-black text-xl">
                    {eur(u.price_per_night_avg ?? u.price_per_night)}
                    <span className="text-[10px] font-normal text-white/50">/nuit</span>
                  </div>
                  {u.estimate_total != null && (
                    <div className="text-[11px] text-[#FFD700]">soit {eur(u.estimate_total)} pour {nights} nuit(s)</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Devis + formulaire */}
        {selected && quote && (
          <div className="mt-7 border-2 border-[#FFD700] bg-white/5 p-5" data-testid="pr-checkout">
            <div className="font-display font-black text-2xl tracking-tight">{selected.label}</div>
            <div className="text-sm text-white/60 flex items-center gap-2">
              <CalendarRange className="h-4 w-4" />{dates.from} → {dates.to} · {quote.nights} nuit(s) · {quote.guests} pers.
            </div>

            {(cfg?.extras || []).filter((e) => e.active !== false).length > 0 && (
              <div className="mt-4" data-testid="pr-extras">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2">
                  Options
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {(cfg.extras || []).filter((e) => e.active !== false).map((e) => {
                    const qty = picks[e.id] || 0;
                    const on = qty > 0;
                    return (
                      <div key={e.id} data-testid={`pr-extra-${e.id}`}
                           className={`border p-3 flex items-center gap-3 transition-colors ${
                             on ? "border-[#FFD700] bg-[#FFD700]/10" : "border-white/15 bg-white/5"}`}>
                        <button type="button" data-testid={`pr-extra-toggle-${e.id}`}
                                onClick={() => setPicks((p) => ({ ...p, [e.id]: on ? 0 : 1 }))}
                                className={`h-8 w-8 shrink-0 grid place-items-center border ${
                                  on ? "bg-[#FFD700] text-black border-[#FFD700]" : "border-white/30 text-white/60"}`}>
                          {on ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold">{e.label}</div>
                          {e.description && <div className="text-[11px] text-white/50">{e.description}</div>}
                          <div className="text-[11px] text-[#FFD700]">
                            {eur(e.price)} {EXTRA_UNIT_LABEL[e.unit] || ""}
                          </div>
                        </div>
                        {on && (e.max_qty || 1) > 1 && (
                          <Input type="number" min="1" max={e.max_qty} value={qty}
                                 data-testid={`pr-extra-qty-${e.id}`}
                                 className="w-16 h-9 bg-[#0A0A0A] border-white/20 text-white"
                                 onChange={(ev) => setPicks((p) => ({
                                   ...p,
                                   [e.id]: Math.max(1, Math.min(parseInt(ev.target.value) || 1, e.max_qty || 1)),
                                 }))} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4 border border-white/15 divide-y divide-white/10 text-sm" data-testid="pr-quote">
              <div className="flex justify-between p-2">
                <span className="text-white/60">
                  {quote.nights} nuit(s) {quote.seasonal ? "· tarif selon la période" : `× ${eur(quote.price_per_night)}`}
                </span>
                <b>{eur(quote.stay_total)}</b>
              </div>
              {quote.seasonal && (quote.nights_detail || []).length > 0 && (
                <div className="p-2 text-[11px]" data-testid="pr-nights-detail">
                  {quote.nights_detail.map((n) => (
                    <div key={n.date} className="flex justify-between text-white/60 py-0.5">
                      <span>
                        {new Date(`${n.date}T12:00:00`).toLocaleDateString("fr-FR",
                          { weekday: "short", day: "2-digit", month: "short" })}
                        {n.season ? <span className="text-[#FFD700]"> · {n.season}</span> : null}
                        {n.weekend ? <span className="text-[#FFD700]"> · week-end</span> : null}
                      </span>
                      <span>{eur(n.price)}</span>
                    </div>
                  ))}
                </div>
              )}
              {quote.tourist_tax_total > 0 && (
                <div className="flex justify-between p-2">
                  <span className="text-white/60">Taxe de séjour ({eur(quote.tourist_tax_per_night)}/nuit/pers.)</span>
                  <b>{eur(quote.tourist_tax_total)}</b>
                </div>
              )}
              {(quote.extras_detail || []).map((x) => (
                <div key={x.id} className="flex justify-between p-2" data-testid={`pr-quote-extra-${x.id}`}>
                  <span className="text-white/60">
                    {x.label}{x.qty > 1 ? ` × ${x.qty}` : ""}
                    <span className="text-white/40"> ({x.unit_label})</span>
                  </span>
                  <b>{eur(x.amount)}</b>
                </div>
              ))}
              <div className="flex justify-between p-2 bg-[#FFD700] text-black">
                <span className="font-bold uppercase tracking-wider text-xs">Total</span>
                <b className="font-display font-black">{eur(quote.total)}</b>
              </div>
              {mode !== "onsite" && quote.deposit_pct > 0 && (
                <>
                  <div className="flex justify-between p-2">
                    <span className="text-white/60">Acompte à payer maintenant ({quote.deposit_pct} %)</span>
                    <b>{eur(quote.amount_due_now)}</b>
                  </div>
                  <div className="flex justify-between p-2">
                    <span className="text-white/60">Solde à régler sur place</span>
                    <b>{eur(quote.balance_on_site)}</b>
                  </div>
                </>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-white/50">Nom et prénom *</Label>
                <Input value={form.client_nom} data-testid="pr-nom" className="bg-[#0A0A0A] border-white/20 text-white"
                       onChange={(e) => setForm({ ...form, client_nom: e.target.value })} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-white/50">Email * (pour recevoir le QR code)</Label>
                <Input type="email" value={form.client_email} data-testid="pr-email"
                       className="bg-[#0A0A0A] border-white/20 text-white"
                       onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-white/50">Téléphone</Label>
                <Input value={form.client_telephone} data-testid="pr-tel"
                       className="bg-[#0A0A0A] border-white/20 text-white"
                       onChange={(e) => setForm({ ...form, client_telephone: e.target.value })} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-white/50">Message (facultatif)</Label>
                <Textarea rows={1} value={form.message} data-testid="pr-message"
                          className="bg-[#0A0A0A] border-white/20 text-white"
                          onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2">Règlement</div>
              <div className="flex flex-wrap gap-2" data-testid="pr-modes">
                {pay.card && (
                  <button type="button" onClick={() => setMode("online")} data-testid="pr-mode-online"
                          className={`flex items-center gap-2 px-4 py-3 border text-sm font-bold transition-colors ${
                            mode === "online" ? "bg-[#FFD700] text-black border-[#FFD700]" : "border-white/25 text-white/70"}`}>
                    <CreditCard className="h-4 w-4" />Carte bancaire ({eur(quote.amount_due_now)})
                  </button>
                )}
                {pay.paypal && (
                  <button type="button" onClick={() => setMode("paypal")} data-testid="pr-mode-paypal"
                          className={`flex items-center gap-2 px-4 py-3 border text-sm font-bold transition-colors ${
                            mode === "paypal" ? "bg-[#FFD700] text-black border-[#FFD700]" : "border-white/25 text-white/70"}`}>
                    <Wallet className="h-4 w-4" />PayPal ({eur(quote.amount_due_now)})
                  </button>
                )}
                {pay.onsite && (
                  <button type="button" onClick={() => setMode("onsite")} data-testid="pr-mode-onsite"
                          className={`flex items-center gap-2 px-4 py-3 border text-sm font-bold transition-colors ${
                            mode === "onsite" ? "bg-[#FFD700] text-black border-[#FFD700]" : "border-white/25 text-white/70"}`}>
                    <Banknote className="h-4 w-4" />Payer sur place
                  </button>
                )}
              </div>
              <div className="text-[11px] text-white/50 mt-2" key={`pr-note-${mode}`} data-testid="pr-mode-note">
                {mode === "online"
                  ? "Paiement sécurisé. Le montant est encaissé directement par l'établissement."
                  : mode === "paypal"
                    ? (pay.paypal === "api"
                      ? "Vous serez redirigé vers PayPal. Le montant est encaissé directement par l'établissement."
                      : "Vous recevrez un lien PayPal avec votre QR code ; la réception confirmera le paiement.")
                    : "Votre QR code servira de preuve de réservation. Vous réglerez à la réception."}
              </div>
            </div>

            {cfg?.cgv && (
              <div className="mt-3 text-[11px] text-white/50 border-l-2 border-white/20 pl-3 whitespace-pre-line"
                   data-testid="pr-cgv">{cfg.cgv}</div>
            )}

            <Button onClick={submit} disabled={busy} data-testid="pr-submit"
                    className="mt-4 h-14 px-8 w-full sm:w-auto bg-[#FFD700] hover:bg-[#E6C200] text-black font-black text-base">
              {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
              <span key={`pr-submit-${busy ? "busy" : mode}`}>
                {busy ? "Traitement…"
                  : mode === "online" ? "Payer et réserver"
                    : mode === "paypal" ? "Réserver et payer avec PayPal"
                      : "Confirmer ma réservation"}
              </span>
            </Button>
          </div>
        )}

        {/* Espace client */}
        <div className="mt-10 border-t border-white/10 pt-6" data-testid="pr-client-area">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mb-2 flex items-center gap-1">
            <Mail className="h-3 w-3" />Déjà réservé ? Retrouvez votre QR code
          </div>
          <div className="flex flex-wrap gap-2 max-w-lg">
            <Input type="email" value={linkEmail} placeholder="votre@email.fr" data-testid="pr-link-email"
                   className="flex-1 min-w-[200px] bg-[#0A0A0A] border-white/20 text-white"
                   onChange={(e) => setLinkEmail(e.target.value)} />
            <Button variant="outline" onClick={requestLink} data-testid="pr-link-submit"
                    className="border-white/25 text-white hover:bg-white hover:text-black">
              Recevoir mon lien
            </Button>
          </div>
          <div className="text-[11px] text-white/40 mt-1">
            Aucun mot de passe : on vous envoie un lien direct vers vos réservations.
          </div>
        </div>
      </div>
    </div>
  );
}
